import Moment from 'moment'
import { extendMoment } from 'moment-range'

export const moment = extendMoment(Moment)

const TIMESTAMP_MIN = 1
const TIMESTAMP_MAX = new Date('3000-01-01T00:00:00').valueOf()

/**
 * Converts a date, date string or timestamp into a canonical date string
 * representation (like "2016-12-24").
 * This representation can be quite useful, since in contrast to the Date object
 * it does not imply any time or timezone and it can seamlessly be turned into
 * a Date object.
 *
 * @param {Moment|Date|string|int|null} value
 * @return {string|null}        Canonical date string or null if no value is given.
 */
export function toCanonicalString(value) {
    if (!value && !(value === 0)) {
        return null
    }

    const date = toDate(value)
    const utcNormalized = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000)

    return utcNormalized.toJSON().replace(/T.*$/, '')
}

/**
 * Parses a given clock time string.
 *
 * @param {string} timeString   Clock time string like "12:30".
 * @return {object} { hour: Number, minute: Number }
 * @throws Error
 */
export function parseTime(timeString, errorMessage) {
    const TIME_REGEX = /^[0-2]?[0-9][:.][0-5][0-9]([:.][0-5][0-9])?(\s(AM|PM))?$/i
    const timeMoment = timeString.match(/(am|pm)$/i) ? moment(timeString, 'hh:mm a') : moment(timeString, 'HH:mm')

    if (!timeMoment.isValid() || !timeString.match(TIME_REGEX)) {
        throw new Error(errorMessage)
    }

    return {
        hour: timeMoment.get('hour'),
        millisecond: 0,
        minute: timeMoment.get('minute'),
        second: 0
    }
}

/**
 * Sets seconds to 59 and milliseconds to 999 if `parsedTime.minute === 59`.
 * Useful for the end value of a time range.
 *
 * @param {object} parsedTime   { hour: number, minute: number }
 * @return {object}             { hour: number, minute: number, second: ?number, millisecond: ?number }
 */
export function roundUpLastMinute(parsedTime) {
    if (parsedTime.minute !== 59) {
        return parsedTime
    }

    return {
        hour: parsedTime.hour,
        millisecond: 999,
        minute: parsedTime.minute,
        second: 59
    }
}

/**
 * @param {Moment|Date|string|int} value
 * @return {Date}
 */
function toDate(value) {
    if (value instanceof moment) {
        return value.toDate()
    } else if (value instanceof Date) {
        return value
    } else {
        return new Date(value)
    }
}

/**
 * @param {Date|DateRange|null} value
 * @return {bool}
 */
export function isValidDateOrRange(value) {
    if (!value) {
        return false
    }
    return 'start' in value && 'end' in value ? isValidDate(value.start) && isValidDate(value.end) : isValidDate(value)
}

/**
 * @param {Date|null} value
 * @return {bool}
 */
export function isValidDate(value) {
    return value && value.valueOf() >= TIMESTAMP_MIN && value.valueOf() <= TIMESTAMP_MAX
}
